/**
 * CategoryRules - Categorization rules management tab (WP-CAT-12)
 *
 * List view of all categorization rules (system + custom).
 *
 * Features:
 * - List all rules with pattern type badge
 * - Toggle active/inactive
 * - Create new rules
 * - Edit/delete custom rules (system rules are read-only)
 * - Test rules against sample text
 *
 * API:
 * - GET /categorization-rules (list)
 * - POST /categorization-rules (create)
 * - PATCH /categorization-rules/{id} (update)
 * - DELETE /categorization-rules/{id} (delete)
 * - POST /categorization-rules/test (test)
 */
import { useState, useCallback } from 'react';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import RulesList from './RulesList';
import RuleForm from './RuleForm';
import RuleTestPanel from './RuleTestPanel';
import { toast } from 'sonner';
import {
  useCategorizationRules,
  useCreateCategorizationRule,
  useUpdateCategorizationRule,
  useDeleteCategorizationRule,
} from '@/hooks/useCategorizationRules';
import type { CategorizationRule, RuleCreateRequest, RuleUpdateRequest } from '@/types/categorizationRule';

// ============================================================================
// Component
// ============================================================================

export default function CategoryRules() {
  // View state
  const [view, setView] = useState<'list' | 'create' | 'edit'>('list');
  const [editingRule, setEditingRule] = useState<CategorizationRule | null>(null);
  const [isTestPanelOpen, setIsTestPanelOpen] = useState(false);

  // Fetch rules
  const { data, isLoading, isError, refetch } = useCategorizationRules();

  // Mutations
  const createMutation = useCreateCategorizationRule();
  const updateMutation = useUpdateCategorizationRule();
  const deleteMutation = useDeleteCategorizationRule();

  // Handle create new rule
  const handleCreate = useCallback(() => {
    setEditingRule(null);
    setView('create');
  }, []);

  // Handle edit rule
  const handleEdit = useCallback((rule: CategorizationRule) => {
    setEditingRule(rule);
    setView('edit');
  }, []);

  // Handle cancel
  const handleCancel = useCallback(() => {
    setEditingRule(null);
    setView('list');
  }, []);

  // Handle form submit
  const handleSubmit = useCallback(
    async (formData: RuleCreateRequest | RuleUpdateRequest) => {
      try {
        if (view === 'edit' && editingRule) {
          await updateMutation.mutateAsync({
            id: editingRule.id,
            data: formData as RuleUpdateRequest,
          });
          toast.success('Rule updated successfully');
        } else {
          await createMutation.mutateAsync(formData as RuleCreateRequest);
          toast.success('Rule created successfully');
        }
        setEditingRule(null);
        setView('list');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'An error occurred';
        toast.error(view === 'edit' ? 'Failed to update rule' : 'Failed to create rule', {
          description: message,
        });
      }
    },
    [view, editingRule, createMutation, updateMutation]
  );

  // Handle delete
  const handleDelete = useCallback(
    async (ruleId: string) => {
      if (!window.confirm('Are you sure you want to delete this rule?')) {
        return;
      }

      try {
        await deleteMutation.mutateAsync(ruleId);
        toast.success('Rule deleted successfully');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'An error occurred';
        toast.error('Failed to delete rule', {
          description: message,
        });
      }
    },
    [deleteMutation]
  );

  // Handle toggle active
  const handleToggleActive = useCallback(
    async (rule: CategorizationRule) => {
      try {
        await updateMutation.mutateAsync({
          id: rule.id,
          data: { isActive: !rule.isActive },
        });
        toast.success(rule.isActive ? 'Rule deactivated' : 'Rule activated');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'An error occurred';
        toast.error('Failed to update rule', {
          description: message,
        });
      }
    },
    [updateMutation]
  );

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center py-12">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-gray-500">Loading rules...</p>
        </div>
      </Card>
    );
  }

  // Error state
  if (isError) {
    return (
      <Card>
        <Alert variant="error" className="mb-4">
          Failed to load categorization rules. Please try again.
        </Alert>
        <Button onClick={() => refetch()} variant="primary">
          Retry
        </Button>
      </Card>
    );
  }

  const rules = data?.rules || [];

  // Create/Edit view
  if (view === 'create' || view === 'edit') {
    return (
      <Card>
        <CardHeader
          title={view === 'edit' ? 'Edit Rule' : 'Create Rule'}
          description={
            view === 'edit'
              ? 'Update the details of your categorization rule.'
              : 'Create a new rule to automatically categorize transactions.'
          }
        />
        <RuleForm
          rule={editingRule}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
        />
      </Card>
    );
  }

  // List view
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Categorization Rules"
          description="Rules automatically assign categories to transactions based on patterns."
          action={
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsTestPanelOpen(true)}
              >
                Test Rules
              </Button>
              <Button variant="primary" size="sm" onClick={handleCreate}>
                Create Rule
              </Button>
            </div>
          }
        />

        {rules.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No categorization rules found.</p>
            <p className="mt-2 text-sm">
              Create a rule to automatically categorize transactions.
            </p>
          </div>
        ) : (
          <RulesList
            rules={rules}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            isUpdating={updateMutation.isPending}
          />
        )}
      </Card>

      {/* Test Panel Modal */}
      {isTestPanelOpen && (
        <RuleTestPanel onClose={() => setIsTestPanelOpen(false)} />
      )}
    </div>
  );
}
